// ============================================================================
// Beacon — due-diligence matrix. A foolproof property condition checklist,
// organized by building SYSTEM and cross-referenced to the CSI MasterFormat
// divisions, plus the Florida-specific inspections (4-point, wind-mit, WDO) and
// public-record checks. Each item can be marked a DEFECT with a remediation
// cost — and every defect cost becomes quantified leverage against the price.
//
// Your inspectors/GCs fill these from their own seats (team portal), so the
// findings and costs update automatically and are attributed to who reported them.
// ============================================================================

import type { Deal, DiligenceItem, DiligenceStatus } from "../types";

/** The template: systems, their CSI division, and the standard points to inspect. */
export const DILIGENCE_TEMPLATE: { system: string; csi: string; points: string[] }[] = [
  { system: "Roof & attic", csi: "Div 7", points: [
    "Roof covering type, age & remaining life", "Active leaks / water staining", "Flashing, valleys & penetrations",
    "Decking / truss condition", "Attic ventilation & insulation", "Soffit / fascia condition" ] },
  { system: "Structure & foundation", csi: "Div 3 / 6", points: [
    "Foundation / slab (cracks, settlement)", "Framing & load-bearing walls", "Floor levelness / deflection",
    "Exterior wall cracks / movement", "Crawlspace / stem wall condition" ] },
  { system: "Exterior envelope", csi: "Div 4 / 7 / 9", points: [
    "Siding / stucco / masonry condition", "Exterior paint & sealant", "Trim, soffits & fascia",
    "Grading & water intrusion at walls", "Balconies / decks / stairs" ] },
  { system: "Windows & doors", csi: "Div 8", points: [
    "Impact-rated / hurricane protection", "Seals, glazing & operation", "Exterior door condition & security",
    "Garage door & opener" ] },
  { system: "Electrical", csi: "Div 16", points: [
    "Panel brand & capacity (no FPE/Zinsco)", "Wiring type (no aluminum branch/knob-&-tube)", "GFCI / AFCI protection",
    "Outlets, switches & fixtures", "Service entrance & grounding", "(4-point item)" ] },
  { system: "Plumbing", csi: "Div 15", points: [
    "Supply lines (no polybutylene)", "Drain / waste / vent condition", "Water heater age & condition",
    "Leaks & water pressure", "Sewer / septic scope", "(4-point item)" ] },
  { system: "HVAC", csi: "Div 15", points: [
    "System age & condition", "Cooling / heating performance", "Ductwork & insulation",
    "Thermostat & controls", "Refrigerant / compressor", "(4-point item)" ] },
  { system: "Interior finishes", csi: "Div 9", points: [
    "Flooring condition", "Walls & ceilings (drywall, cracks)", "Interior paint",
    "Trim & doors", "Stairs & railings" ] },
  { system: "Cabinets & countertops", csi: "Div 6 / 12", points: [
    "Kitchen cabinets & counters", "Bath vanities", "Hardware & drawer function" ] },
  { system: "Appliances & equipment", csi: "Div 11", points: [
    "Range / oven", "Refrigerator", "Dishwasher", "Washer / dryer", "Water softener / other" ] },
  { system: "Site, drainage & landscaping", csi: "Div 2", points: [
    "Site grading & drainage", "Driveway / walkways", "Fencing & gates",
    "Trees / hazards near structure", "Retaining walls" ] },
  { system: "Fire / life safety", csi: "Div 10 / 13", points: [
    "Smoke & CO detectors", "Egress windows & exits", "Handrails / guardrails",
    "Electrical / fire hazards" ] },
  { system: "Moisture & mold", csi: "Env", points: [
    "Visible mold / mildew", "Moisture intrusion / humidity", "Prior water damage / staining",
    "Air-quality concerns" ] },
  { system: "WDO / termite", csi: "Env", points: [
    "Active infestation (WDO report)", "Prior / treated infestation", "Wood damage from pests",
    "Conducive conditions (wood-soil contact)" ] },
  { system: "Insurance certifications", csi: "FL", points: [
    "4-point inspection (roof/elec/plumb/HVAC)", "Wind-mitigation report", "Roof certification / remaining life",
    "Flood / elevation certificate" ] },
  { system: "Legal & public records", csi: "Div 1", points: [
    "Open / expired permits", "Code violations / open cases", "Unpermitted work / additions",
    "Liens & judgments (beyond mortgage)", "Lis pendens / litigation", "Zoning & use compliance" ] },
];

export const DILIGENCE_SYSTEMS = DILIGENCE_TEMPLATE.map((t) => t.system);

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
/** Deterministic id from system+label so template merges stay stable (no Date/random). */
export function diligenceId(system: string, label: string): string {
  return `dd_${slug(system)}__${slug(label)}`;
}

/** The full seeded checklist, everything "to inspect". */
export function defaultDiligence(): DiligenceItem[] {
  const items: DiligenceItem[] = [];
  for (const t of DILIGENCE_TEMPLATE) {
    for (const label of t.points) {
      items.push({ id: diligenceId(t.system, label), system: t.system, csi: t.csi, label, status: "todo" });
    }
  }
  return items;
}

/** Merge stored findings onto the current template so new points always appear,
 *  and any custom (non-template) items the team added are preserved. */
export function withDiligence(deal: Deal): DiligenceItem[] {
  const stored = deal.diligence ?? [];
  const byId = new Map(stored.map((d) => [d.id, d]));
  const base = defaultDiligence().map((d) => ({ ...d, ...(byId.get(d.id) ?? {}) }));
  const templateIds = new Set(base.map((d) => d.id));
  const custom = stored.filter((d) => !templateIds.has(d.id));
  return [...base, ...custom];
}

export const isDefect = (d: DiligenceItem): boolean => d.status === "defect";

/** Defects that count as price leverage (a defect with a cost, not opted out). */
export function negotiableDefects(items: DiligenceItem[]): DiligenceItem[] {
  return items.filter((d) => isDefect(d) && d.negotiable !== false && (d.cost ?? 0) > 0);
}

/** Total documented remediation cost of the negotiable defects — the leverage $. */
export function totalDefectCost(items: DiligenceItem[]): number {
  return negotiableDefects(items).reduce((s, d) => s + (d.cost ?? 0), 0);
}

export function diligenceStats(items: DiligenceItem[]): { inspected: number; total: number; defects: number; safety: number; leverage: number } {
  return {
    inspected: items.filter((d) => d.status !== "todo").length,
    total: items.length,
    defects: items.filter(isDefect).length,
    safety: items.filter((d) => isDefect(d) && d.severity === "safety").length,
    leverage: totalDefectCost(items),
  };
}
